/**
 * Parent (CMMD) → iframe commands for embed chrome moved onto the CMMD AppBar.
 * Trusted origins match the handoff listener. Paths are allowlisted so a
 * compromised parent cannot drive arbitrary Forge routes.
 *
 * Ready fires before React mounts (handoff boot). Parent may flush navigate in
 * that window. Buffer those commands until SidebarAccountFooter subscribes.
 */

import { isCmmdEmbedMode } from "./cmmdEmbedMode";
import { CMMD_RUNTIME_ENVIRONMENT_PREFIX } from "../environments/cmmdRuntimeEnvironmentId";

export const CMMD_EMBED_COMMAND_TYPE = "cmmd-forge-embed-command";

export type CmmdEmbedNavigateCommand = {
  readonly type: typeof CMMD_EMBED_COMMAND_TYPE;
  readonly action: "navigate";
  readonly path: string;
  readonly search?: Readonly<Record<string, string>>;
};

export type CmmdEmbedOpenFeedbackCommand = {
  readonly type: typeof CMMD_EMBED_COMMAND_TYPE;
  readonly action: "open-feedback";
};

export type CmmdEmbedHostCommand = CmmdEmbedNavigateCommand | CmmdEmbedOpenFeedbackCommand;

const ALLOWED_NAVIGATE_PATHS = new Set(["/settings", "/pull-requests", "/plugins", "/usage"]);

/** Matches Forge runtime thread paths under the shared environment-id prefix. */
const CMMD_RUNTIME_THREAD_PATH = new RegExp(
  `^/${CMMD_RUNTIME_ENVIRONMENT_PREFIX}([A-Za-z0-9][A-Za-z0-9._-]{0,127})/([A-Za-z0-9][A-Za-z0-9._-]{0,127})$`,
);

function isCmmdHost(hostname: string): boolean {
  return hostname === "cmmd.ai" || hostname.endsWith(".cmmd.ai");
}

function isLocalCmmdDevHost(hostname: string, port: string): boolean {
  const localName = hostname === "localhost" || hostname === "127.0.0.1";
  const localPort = port === "3015" || port === "3000" || port === "";
  return localName && localPort;
}

function isTrustedCmmdEmbedCommandOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    if (isCmmdHost(url.hostname)) return url.protocol === "https:";
    return isLocalCmmdDevHost(url.hostname, url.port) && url.protocol.startsWith("http");
  } catch {
    return false;
  }
}

function normalizeNavigatePath(path: string): string | null {
  const trimmed = path.trim();
  if (!trimmed.startsWith("/")) return null;
  if (trimmed.includes("://") || trimmed.includes("\\")) return null;
  const pathname = trimmed.split("?")[0]?.split("#")[0] ?? "";
  if (pathname === "/settings" || pathname.startsWith("/settings/")) {
    return pathname;
  }
  if (ALLOWED_NAVIGATE_PATHS.has(pathname)) {
    return pathname;
  }
  // Project task open-work under the shared runtime environment-id prefix.
  if (CMMD_RUNTIME_THREAD_PATH.test(pathname)) {
    return pathname;
  }
  return null;
}

function parseNavigateSearch(value: unknown): Readonly<Record<string, string>> | undefined {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return undefined;
  }
  const entries: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (typeof entry === "string") {
      entries[key] = entry;
    }
  }
  return Object.keys(entries).length > 0 ? entries : undefined;
}

export function parseCmmdEmbedHostCommand(data: unknown): CmmdEmbedHostCommand | null {
  if (typeof data !== "object" || data === null) return null;
  const record = data as Record<string, unknown>;
  if (record.type !== CMMD_EMBED_COMMAND_TYPE) return null;
  if (record.action === "open-feedback") {
    return { type: CMMD_EMBED_COMMAND_TYPE, action: "open-feedback" };
  }
  if (record.action !== "navigate" || typeof record.path !== "string") return null;
  const path = normalizeNavigatePath(record.path);
  if (path === null) return null;
  const search = parseNavigateSearch(record.search);
  return search
    ? { type: CMMD_EMBED_COMMAND_TYPE, action: "navigate", path, search }
    : { type: CMMD_EMBED_COMMAND_TYPE, action: "navigate", path };
}

export type CmmdEmbedHostCommandHandlers = {
  readonly onNavigate: (path: string, search?: Readonly<Record<string, string>>) => void;
  readonly onOpenFeedback: () => void;
};

type ActiveHandlers = CmmdEmbedHostCommandHandlers;

let earlyListenerAttached = false;
let activeHandlers: ActiveHandlers | null = null;
let bufferedCommands: CmmdEmbedHostCommand[] = [];

function dispatchHostCommand(handlers: ActiveHandlers, command: CmmdEmbedHostCommand): void {
  if (command.action === "open-feedback") {
    handlers.onOpenFeedback();
    return;
  }
  handlers.onNavigate(command.path, command.search);
}

function onEarlyHostMessage(event: MessageEvent): void {
  if (!isTrustedCmmdEmbedCommandOrigin(event.origin)) return;
  const command = parseCmmdEmbedHostCommand(event.data);
  if (command === null) return;
  if (activeHandlers) {
    dispatchHostCommand(activeHandlers, command);
    return;
  }
  bufferedCommands = [...bufferedCommands, command];
}

/**
 * Start buffering parent commands as soon as embed mode is known, before React
 * mounts. Safe to call more than once.
 */
export function ensureCmmdEmbedHostCommandEarlyListener(): void {
  if (typeof window === "undefined") return;
  if (earlyListenerAttached) return;
  if (!isCmmdEmbedMode()) return;
  if (window.parent === window) return;
  earlyListenerAttached = true;
  window.addEventListener("message", onEarlyHostMessage);
}

/** Test-only: drop buffered commands and early listener state. */
export function __resetCmmdEmbedHostCommandBufferForTests(): void {
  bufferedCommands = [];
  activeHandlers = null;
  if (earlyListenerAttached && typeof window !== "undefined") {
    window.removeEventListener("message", onEarlyHostMessage);
  }
  earlyListenerAttached = false;
}

/**
 * Subscribe to CMMD parent commands while this window is an embed.
 * Returns an unsubscribe function. No-op when not embedded or not framed.
 * Replays any commands buffered before this subscribe.
 */
export function subscribeCmmdEmbedHostCommands(handlers: CmmdEmbedHostCommandHandlers): () => void {
  if (typeof window === "undefined") return () => undefined;
  if (!isCmmdEmbedMode()) return () => undefined;
  if (window.parent === window) return () => undefined;

  ensureCmmdEmbedHostCommandEarlyListener();
  activeHandlers = handlers;
  const pending = bufferedCommands;
  bufferedCommands = [];
  for (const command of pending) {
    dispatchHostCommand(handlers, command);
  }

  return () => {
    if (activeHandlers === handlers) {
      activeHandlers = null;
    }
  };
}
